import { FC, useEffect, useState } from "react";
import {
  Box,
  Flex,
  TextArea,
  TextField,
  Text,
  IconButton,
  AspectRatio,
  Button,
  Spinner,
} from "@radix-ui/themes";
import "@github/markdown-toolbar-element";
import MarkdownToolbar from "@/features/news/components/manage/MarkdownToolbar";
import { Cross1Icon, ImageIcon } from "@radix-ui/react-icons";
import { ImageUpload } from "@/components/ImageUpload/ImageUpload";
import slugify from "react-slugify";
import { NewsProps } from "../../types/NewsTypes";

interface NewsFormProps {
  values: NewsProps;
  setValues: (values: NewsProps) => void;
  submit: (values: NewsProps) => void;
  loading: boolean;
  isEdit: boolean;
}

const NewsForm: FC<NewsFormProps> = ({
  values,
  setValues,
  submit,
  loading,
  isEdit,
}): JSX.Element => {
  const [preview, setPreview] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (values.title && !isEdit) {
      setValues({
        ...values,
        slug: slugify(values.title),
      });
    }
  }, [values.title]);

  const setFieldValue = (targetName: string, value: any) => {
    setValues({
      ...values,
      [targetName]: value,
    });
  };

  useEffect(() => {
    if (values.imageURL) {
      if (values.imageURL instanceof Blob || values.imageURL instanceof File) {
        const objectUrl = URL.createObjectURL(values.imageURL);
        setPreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
      } else if (typeof values.imageURL === "string") {
        setPreview(values.imageURL);
      }
    } else {
      setPreview(undefined);
    }
  }, [values.imageURL]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await submit(values);
  };

  return (
    <>
      <Flex direction="column" gap="3">
        {/* Title */}
        <Flex direction="column" gap="1">
          <Text as="label" htmlFor="news-title" size="2">
            Headline
          </Text>
          <TextField.Root
            id="news-title"
            placeholder="Enter a title"
            variant="soft"
            color="gray"
            value={values.title}
            onChange={(e) => setValues({ ...values, title: e.target.value })}
          />
        </Flex>

        {/* Excerpt */}
        <Flex direction="column" gap="1">
          <Text as="label" htmlFor="news-excerpt" size="2">
            Excerpt
          </Text>
          <TextField.Root
            id="news-excerpt"
            placeholder="Enter a short description"
            variant="soft"
            color="gray"
            value={values.intro}
            onChange={(e) => setValues({ ...values, intro: e.target.value })}
          />
        </Flex>

        {/* Text Area */}
        <Flex direction="column" gap="1">
          <Text as="label" htmlFor="news-content" size="2">
            Content
          </Text>
          <Box position="relative">
            <TextArea
              color="gray"
              spellCheck={false}
              id="news-content"
              variant="soft"
              rows={10}
              style={{ paddingTop: 48 }}
              value={values.content}
              onChange={(e) =>
                setValues({ ...values, content: e.target.value })
              }
            />
            <Box position="absolute" m="2" top="0" left="0" right="0">
              <MarkdownToolbar />
            </Box>
          </Box>
        </Flex>
        {/* Preview markdown */}
        {/* {values.content && <ReactMarkdown>{values.content}</ReactMarkdown>} */}

        {/* Image */}
        <Flex direction="column" gap="1">
          <Text as="label" size="2">
            Image
          </Text>
          <Flex gap="2">
            {/* How uploaded images look like */}
            {preview && (
              <Box width="120px" height="120px" position="relative">
                <AspectRatio ratio={1 / 1}>
                  <img
                    src={preview}
                    alt={values.title || "Image"}
                    width="200"
                    height="200"
                    style={{
                      borderRadius: "var(--radius-2)",
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </AspectRatio>
                {/* Remove image button */}
                <Box position="absolute" inset="0.25rem 0.25rem auto auto">
                  <IconButton
                    highContrast
                    color="red"
                    variant="solid"
                    size="2"
                    onClick={() => {
                      setFieldValue("imageURL", undefined);
                    }}
                  >
                    <Cross1Icon />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Image upload button */}
            {!preview && (
              <ImageUpload
                acceptType="image/*"
                setIsLoading={() => {}}
                setFieldValue={setFieldValue}
                setType={() => {}}
                value={values.imageURL}
                name="imageURL"
              >
                <Flex
                  align="center"
                  justify="center"
                  style={{
                    border: "1px solid var(--gray-5)",
                    borderRadius: "var(--radius-2)",
                    width: "120px",
                    height: "120px",
                  }}
                >
                  <Box>
                    <IconButton
                      highContrast
                      color="gray"
                      variant="soft"
                      size="2"
                      onClick={() => {
                        // Trigger the file input click event
                        (
                          document.querySelector(
                            'input[type="file"]'
                          ) as HTMLInputElement
                        ).click();
                      }}
                    >
                      <ImageIcon />
                    </IconButton>
                  </Box>
                </Flex>
              </ImageUpload>
            )}
          </Flex>
        </Flex>

        {/* Submit */}
        <Flex gap="2" justify="start">
          {/* <Button variant="soft">Preview</Button> */}
          <Button variant="solid" onClick={handleSubmit}>
            {!loading ? isEdit ? "Update" : "Submit" : <Spinner />}
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

export default NewsForm;
